import queryString from 'query-string';
import { createElement, MouseEventHandler, ReactNode } from 'react';
import { matchPath } from 'react-router';

import { nuiCtx } from '../../common/initialization';
import { NikeRedirect, NikeRoute } from '../../common/routing/types';
import { isEmpty } from 'lodash';

const httpRegex = new RegExp('^http');
const nikeRegex = new RegExp('nike.sk$');
const pdfRegex = new RegExp('.pdf$');

export function interceptNavigation(
    e: Parameters<MouseEventHandler>[0],
    href: string,
    onClick?: MouseEventHandler<any>,
    allowReplace?: boolean
) {
    if (!href) {
        e.preventDefault();
        e.stopPropagation();
        return;
    }
    // do not intercept anchor links
    if (href[0] === '#') {
        return;
    }
    if (onClick) {
        onClick(e);
    }

    const a = document.createElement('a');
    a.href = href;
    const { hostname, pathname } = a;
    // pdf dokumenty sa otvaraju v novom okne
    if (pdfRegex.test(pathname)) {
        window.open(href);
        e.preventDefault();
        e.stopPropagation();
        return true;
    }

    // co zacina http a nie je nike.sk otvarame v novom okne
    if (httpRegex.test(href) && !nikeRegex.test(hostname)) {
        window.open(href);
        e.preventDefault();
        e.stopPropagation();
        return true;
    }

    if (!isEmpty(e?.currentTarget.getAttribute('target'))) {
        return true;
    }

    if (e?.type === 'click' && (e?.ctrlKey || e?.metaKey || e?.shiftKey || e?.altKey)) {
        return true;
    }

    if (hostname === 'localhost' || nikeRegex.test(hostname)) {
        const { routes, redirects, navigate } = nuiCtx.routing;
        const match = matchRoute(pathname, routes, redirects);
        if (match) {
            const { query } = queryString.parseUrl(href);
            navigate(match.route, match.params, query, null, allowReplace);
            e?.preventDefault();
            e?.stopPropagation();
            return true;
        }
    }
    window.location.href = href;
}

export function interceptAnchorClickEvent(e, routes, redirects) {
    const target = e.target || e.srcElement;

    if (target && target.tagName === 'A') {
        const href = target.getAttribute('href');
        return interceptNavigation(e, href, routes, redirects);
    }
}

function matchRoute(url: string, routes: NikeRoute[], redirects: NikeRedirect[]) {
    url = redirects[url] || url;
    for (const key in routes) {
        const route = routes[key];
        const match = matchPath(url, { path: route.path, exact: true });
        if (match) {
            return { route, params: match.params };
        }
    }
    return null;
}

interface LinkInterceptorProps extends Record<string, any> {
    href?: string;
    onClick?: MouseEventHandler;
    element?: string;
    children?: ReactNode;
    allowReplace?: boolean;
}

function LinkInterceptor({ href, onClick, element = 'a', children, allowReplace, ...rest }: LinkInterceptorProps) {
    function onClickHandler(e) {
        interceptNavigation(e, href, onClick, allowReplace);
    }
    const elementProps = {
        ...rest,
        href: element === 'a' ? href : null,
        onClick: onClickHandler
    };

    return createElement(element, elementProps, children);
}

export default LinkInterceptor;
